import { observer } from "mobx-react";
import React from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  useLocation,
} from "react-router-dom";
import AppPage from "../components/app/index";
import ErrorPage from "../error-page";
import { useStore } from "../hooks";
import ForbiddenPage from "../components/forbidenPage"; // Updated import
import { getSearchWithoutAuth0CallbackParams } from "../utils/auth0Cache";
//v2
import TemplateEditor from "../elements/v2/TemplateEditor/TemplateEditor";

import HomePageV2 from "../pages/v2/HomePageV2";

import { Loading } from "../components/loading";
import ProfilePageV2 from "../pages/v2/ProfilePage/ProfilePage";
import { Signin } from "../components";
import AgentsViewPageV2 from "../pages/v2/AgentsViewV2";
import BlastCampaignPage from "../pages/v2/BlastCampaignPage";
import DepartmentsPage from "../pages/v2/Departments";
import DrivesPage from "../pages/v2/DrivesPage";
import KnowledgePage from "../pages/v2/Knowledge";
import PlaygroundPage from "../pages/v2/Playground";
import PlaygroundPageV2 from "../pages/v2/PlaygroundV2";
import PMSystemPage from "../pages/v2/PMSystemPage/PMSystemPage";
import PromptManagementSystemPage from "../pages/v2/PromptManagementSystemPage/PromptManagementSystemPage";
import TriggersPage from "../pages/v2/TriggersPage";
import UCPPageV2 from "../pages/v2/UCPPage";
import UCPPageV1 from "../pages/v2/UCPPageV1";
import UserManagementPage from "../pages/v2/UserManagement";
import WorkspacePage from "../pages/v2/Workspace";
import DashboardBuilderPage from "../pages/v2/DashboardBuilderPage";
import {
  DashboardDetail,
  DashboardEditor,
  DashboardList,
  DashboardNew,
  TemplateList,
} from "../pages/v2/DashboardBuilderPage/pages";
import { PermissionCodes } from "../utils/enums";

const ProtectedRoute = observer(
  ({ element: Element, permissionCode, ...rest }) => {
    const { authStore } = useStore();

    const hasExplicitPermission =
      authStore.isGroupSuperAdmin ||
      authStore.isGroupAdmin ||
      (authStore.userPermissions.length > 0 &&
        authStore.hasPermission(permissionCode, "VIEW"));

    // Allow HOME when authenticated: with API token, or at least Cognito (avoids Access Denied
    // when token/user API isn't configured or doesn't return admin or permissions yet)
    const isMinimalAuthForHome =
      permissionCode === "HOME" && authStore.userId && !authStore.isError;

    const allowAccess = hasExplicitPermission || isMinimalAuthForHome;

    return (
      <>
        {authStore.isLoading && <Loading />}
        {!authStore.isLoading && (
          <>{(allowAccess ? <Element {...rest} /> : <ForbiddenPage />)}</>
        )}
      </>
    );
  }
);

const AppLayout = () => (
  <Signin>
    <AppPage>
      <Outlet />
    </AppPage>
  </Signin>
);

/** Preserves non-OAuth query params (e.g. ?error=) but drops one-time ?code=&state= from the callback. */
const IndexRedirectToHome = () => {
  const { search } = useLocation();
  const { search: cleanSearch } = getSearchWithoutAuth0CallbackParams(search);
  return <Navigate to={`/home${cleanSearch}`} replace />;
};

const routes = createRoutesFromElements(
    <>
      <Route path={"/*"} element={<AppLayout />} errorElement={<ErrorPage />}>
        <Route
          path="forbidden"
          element={<ForbiddenPage />}
          errorElement={<ErrorPage />}
        />

        <Route
          index
          element={<IndexRedirectToHome />}
          errorElement={<ErrorPage />}
        />

        <Route
          path="home"
          element={
            <ProtectedRoute element={HomePageV2} permissionCode="HOME" />
          }
          errorElement={<ErrorPage />}
        />

        <Route
          path="dashboards"
          element={
            <ProtectedRoute
              element={DashboardBuilderPage}
              permissionCode={PermissionCodes.DASHBOARDS_VIEW}
            />
          }
          errorElement={<ErrorPage />}
        >
          <Route index element={<DashboardList />} />
          <Route path="templates" element={<TemplateList />} />
          <Route path="new" element={<DashboardNew />} />
          <Route path=":dashboardId" element={<Outlet />}>
            <Route index element={<DashboardDetail />} />
            <Route path="edit" element={<DashboardEditor />} />
          </Route>
        </Route>

        <Route path="v1">
          <Route
            path="ucp/*"
            element={<UCPPageV1 />}
            errorElement={<ErrorPage />}
          />
        </Route>

        <Route path="v2">
          <Route
            path="home"
            element={
              <ProtectedRoute element={HomePageV2} permissionCode="HOME" />
            }
            errorElement={<ErrorPage />}
          />
          <Route
            path="template/*"
            element={
              <ProtectedRoute
                element={TemplateEditor}
                permissionCode="TEMPLATE_EDIT"
              />
            }
          />

          <Route
            path="profile/*"
            element={
              <ProtectedRoute
                element={ProfilePageV2}
                permissionCode="PROFILE_VIEW"
              />
            }
            errorElement={<ErrorPage />}
          />
          <Route
            path="prompt-management-system/*"
            element={
              <ProtectedRoute
                element={PromptManagementSystemPage}
                permissionCode="AGENTS_VIEW"
              />
            }
            errorElement={<ErrorPage />}
          />
          <Route
            path="pms/*"
            element={
              <ProtectedRoute
                element={PMSystemPage}
                permissionCode="PMS_VIEW"
              />
            }
            errorElement={<ErrorPage />}
          />
          <Route
            path="workflows/*"
            element={<TriggersPage />}
            errorElement={<ErrorPage />}
          />
          <Route
            path="ucp/*"
            element={<UCPPageV2 />}
            errorElement={<ErrorPage />}
          />
          <Route
            path="playground/*"
            element={
              <ProtectedRoute
                element={PlaygroundPage}
                permissionCode={PermissionCodes.PLAYGROUND_VIEW}
              />
            }
            errorElement={<ErrorPage />}
          />
          <Route
            path="playground2/*"
            element={
              <ProtectedRoute
                element={PlaygroundPageV2}
                permissionCode={PermissionCodes.PLAYGROUND_VIEW}
              />
            }
            errorElement={<ErrorPage />}
          />
          {/* <Route
            path="agentsView/*"
            element={<AgentsViewPage />}
            errorElement={<ErrorPage />}
          /> */}
          <Route
            path="agentsView/*"
            element={<AgentsViewPageV2 />}
            errorElement={<ErrorPage />}
          />
          <Route
            path="drives/*"
            element={<DrivesPage />}
            errorElement={<ErrorPage />}
          />
          <Route
            path="user-management/*"
            element={
              <ProtectedRoute
                element={UserManagementPage}
                permissionCode="USER_MANAGEMENT_VIEW"
              />
            }
            errorElement={<ErrorPage />}
          />
          <Route
            path="workspace/*"
            element={<WorkspacePage />}
            errorElement={<ErrorPage />}
          />
          <Route
            path="departments/*"
            element={<DepartmentsPage />}
            errorElement={<ErrorPage />}
          />
          <Route
            path="knowledge/*"
            element={
              <ProtectedRoute
                element={KnowledgePage}
                permissionCode="KNOWLEDGE_VIEW"
              />
            }
            errorElement={<ErrorPage />}
          />
          <Route
            path="blast-campaigns/*"
            element={
              <ProtectedRoute
                element={BlastCampaignPage}
                permissionCode="BLAST_CAMPAIGNS_VIEW"
              />
            }
            errorElement={<ErrorPage />}
          />
        </Route>
      </Route>
    </>
);

const RoutesMenu = createBrowserRouter(routes);

export default RoutesMenu;
